import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, TextField, FormControl, InputLabel } from '@mui/material';
import HelpIcon from './HelpIcon';

const cities = [
    { name: 'Custom', lat: null, lon: null },
    { name: 'New York', lat: 40.7128, lon: -74.0060 },
    { name: 'London', lat: 51.5074, lon: -0.1278 },
    { name: 'Tokyo', lat: 35.6895, lon: 139.6917 },
    { name: 'Sydney', lat: -33.8688, lon: 151.2093 },
    { name: 'Los Angeles', lat: 34.0522, lon: -118.2437 },
    { name: 'Mumbai', lat: 19.0760, lon: 72.8777 },
    { name: 'Delhi', lat: 28.7041, lon: 77.1025 },
];

const LocationSettingsDialog = ({ open, onClose, onSave, currentPreferences }) => {
    const [selectedCity, setSelectedCity] = useState('New York');
    const [latitude, setLatitude] = useState(40.7128);
    const [longitude, setLongitude] = useState(-74.0060);
    const [isCustom, setIsCustom] = useState(false);

    useEffect(() => {
        if (open && currentPreferences) {
            const prefName = currentPreferences.referenceLocationName || 'Custom';
            const cityExists = cities.some(c => c.name === prefName);
            
            if (cityExists && prefName !== 'Custom') {
                setSelectedCity(prefName);
                setIsCustom(false);
            } else {
                setSelectedCity('Custom');
                setIsCustom(true);
            }

            setLatitude(currentPreferences.referenceLatitude);
            setLongitude(currentPreferences.referenceLongitude);
        }
    }, [open, currentPreferences]);

    const handleCityChange = (event) => {
        const cityName = event.target.value;
        setSelectedCity(cityName);

        if (cityName === 'Custom') {
            setIsCustom(true);
        } else {
            setIsCustom(false);
            const city = cities.find(c => c.name === cityName);
            if (city) {
                setLatitude(city.lat);
                setLongitude(city.lon);
            }
        }
    };

    const handleSave = () => {
        onSave({ 
            referenceLatitude: latitude, 
            referenceLongitude: longitude, 
            referenceLocationName: selectedCity 
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Location Settings</span>
        <HelpIcon helpKey="locationSettingsDialog" />
      </DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <InputLabel>Location</InputLabel>
                    <Select value={selectedCity} onChange={handleCityChange}>
                        {cities.map(city => (
                            <MenuItem key={city.name} value={city.name}>{city.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Latitude"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={latitude || ''}
                    onChange={(e) => setLatitude(parseFloat(e.target.value))}
                    disabled={!isCustom}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Longitude"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={longitude || ''}
                    onChange={(e) => setLongitude(parseFloat(e.target.value))}
                    disabled={!isCustom}
                    InputLabelProps={{ shrink: true }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

LocationSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentPreferences: PropTypes.shape({
    referenceLatitude: PropTypes.number,
    referenceLongitude: PropTypes.number,
    referenceLocationName: PropTypes.string,
  }),
};

export default LocationSettingsDialog;